import React from "react";
import { Box, TextField, Typography, FormControlLabel, Checkbox } from "@mui/material";

const trimValue = (value) => String(value || "").trim();

const normalizeDrivetrain = (value) => {
  const raw = trimValue(value);
  const normalized = raw.toLowerCase();
  if (!raw) return "";
  if (normalized.includes("four") || normalized.includes("4wd") || normalized.includes("4x4")) return "4WD";
  if (normalized.includes("all") || normalized.includes("awd")) return "AWD";
  if (normalized.includes("front") || normalized.includes("fwd")) return "FWD";
  if (normalized.includes("rear") || normalized.includes("rwd")) return "RWD";
  if (
    normalized.includes("two") ||
    normalized.includes("2wd") ||
    normalized.includes("2-wheel") ||
    normalized.includes("2 wheel") ||
    normalized.includes("4x2")
  ) {
    return "2WD";
  }
  return raw;
};

export const createVehicleInitialValues = (userId = "") => ({
  company: "",
  make: "",
  model: "",
  tag_number: "",
  customer: "",
  no_vin: false,
  vin_number: "",
  year: "",
  engine_size: "",
  engine_cylinders: "",
  drivetrain: "",
  user_id: userId,
});

export const buildVehicleCustomerOptions = (customers = []) =>
  (Array.isArray(customers) ? customers : []).map((customer) => ({
    label: `${customer.name || "Customer"}${customer.phone ? ` (${customer.phone})` : ""}`,
    value: customer._id,
  }));

export const prepareVehicleFormValues = (value = {}, userId = "") => ({
  ...createVehicleInitialValues(userId),
  ...value,
  make: value.make || value.name || "",
  customer: value?.customer?._id || value?.customer || "",
  no_vin: Boolean(value.no_vin || (!value.vin_number && value._id)),
  vin_number: String(value.vin_number || "").toUpperCase(),
  drivetrain: normalizeDrivetrain(value.drivetrain),
  user_id: value.user_id || userId,
});

export const buildVehiclePayload = (value = {}, customers = [], userId = "") => {
  const prepared = prepareVehicleFormValues(value, userId);
  const customerId = prepared.customer?._id || prepared.customer || "";
  const explicitCustomer =
    prepared.customer && typeof prepared.customer === "object"
      ? prepared.customer
      : null;
  const customerObject = (Array.isArray(customers) ? customers : []).find(
    (customer) => String(customer?._id || "") === String(customerId),
  );

  return {
    ...prepared,
    customer: customerObject || explicitCustomer || null,
    no_vin: Boolean(prepared.no_vin),
    vin_number: prepared.no_vin
      ? ""
      : String(prepared.vin_number || "").toUpperCase(),
    make: trimValue(prepared.make),
    model: trimValue(prepared.model),
    year: trimValue(prepared.year),
    engine_size: trimValue(prepared.engine_size),
    engine_cylinders: trimValue(prepared.engine_cylinders),
    drivetrain: normalizeDrivetrain(prepared.drivetrain),
    tag_number: trimValue(prepared.tag_number),
    company: trimValue(prepared.company),
    user_id: prepared.user_id || userId,
  };
};

const titleCase = (val) => {
  if (typeof val !== "string") return val;
  return val.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
};

const formatEngineSize = (val) => {
  const num = Number(val);
  if (!Number.isFinite(num)) return val || "";
  return (Math.round(num * 10) / 10).toString();
};

export const mapVinDecodedVehicle = (mapped = {}, currentValues = {}, onlyFillEmpty = true) => {
  const normalizedMake = titleCase(mapped.make || mapped.name);
  const normalizedEngineSize = formatEngineSize(mapped.engineSize);

  return onlyFillEmpty
    ? {
        year: currentValues.year || mapped.year || "",
        make: currentValues.make || normalizedMake || "",
        model: currentValues.model || mapped.model || "",
        engine_size: currentValues.engine_size || normalizedEngineSize || "",
        engine_cylinders:
          currentValues.engine_cylinders || mapped.engineCylinders || "",
        drivetrain:
          currentValues.drivetrain || normalizeDrivetrain(mapped.drivetrain),
      }
    : {
        year: mapped.year || currentValues.year || "",
        make: normalizedMake || currentValues.make || "",
        model: mapped.model || currentValues.model || "",
        engine_size: normalizedEngineSize || currentValues.engine_size || "",
        engine_cylinders:
          mapped.engineCylinders || currentValues.engine_cylinders || "",
        drivetrain:
          normalizeDrivetrain(mapped.drivetrain) || currentValues.drivetrain,
      };
};

export const getVehicleFormFields = ({
  customerOptions = [],
  customersLoaded = false,
  onlyFillEmptyVin = true,
  setOnlyFillEmptyVin,
  onlyFillEmptyRef,
  onVinInputChange,
  onVinChange,
  onVinBlur,
  vinLoading = false,
  vinError = "",
  formikValuesRef,
  formikSetFieldValueRef,
}) => [
  {
    name: "customer",
    label: "Select Customer (Optional)",
    type: "select",
    options: customerOptions,
    disabled: !customersLoaded,
  },
  {
    name: "no_vin",
    type: "custom",
    component: ({ values, setFieldValue }) => (
      <FormControlLabel
        control={
          <Checkbox
            checked={Boolean(values.no_vin)}
            onChange={(e) => {
              const checked = e.target.checked;
              setFieldValue("no_vin", checked);
              if (checked) {
                setFieldValue("vin_number", "");
                onVinInputChange?.("");
              }
            }}
          />
        }
        label="No VIN"
        sx={{ mt: -0.5 }}
      />
    ),
  },
  {
    name: "vin_number",
    label: "VIN Number",
    type: "custom",
    component: ({ values, setFieldValue, errors, touched }) => {
      if (formikValuesRef) formikValuesRef.current = values;
      if (formikSetFieldValueRef) formikSetFieldValueRef.current = setFieldValue;

      return (
        <Box>
          <TextField
            fullWidth
            name="vin_number"
            label="VIN Number"
            size="small"
            value={values.vin_number || ""}
            onChange={(e) => {
              const upper = String(e.target.value || "").toUpperCase();
              setFieldValue("vin_number", upper);
              if (values.no_vin) {
                setFieldValue("no_vin", false);
              }
              onVinInputChange?.(upper);
              onVinChange?.(upper);
            }}
            onBlur={(e) => {
              const upper = String(e.target.value || "").toUpperCase();
              setFieldValue("vin_number", upper);
              onVinInputChange?.(upper);
              if (!values.no_vin) {
                onVinBlur?.(upper);
              }
            }}
            disabled={Boolean(values.no_vin)}
            error={touched.vin_number && Boolean(errors.vin_number)}
            helperText={
              values.no_vin
                ? "VIN not required for this vehicle"
                : (touched.vin_number && errors.vin_number) || ""
            }
          />
          {!values.no_vin && vinLoading && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.5, display: "block" }}
            >
              Looking up VIN...
            </Typography>
          )}
          {!values.no_vin && vinError && (
            <Typography
              variant="caption"
              color="error"
              sx={{ mt: 0.5, display: "block" }}
            >
              {vinError}
            </Typography>
          )}
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={onlyFillEmptyVin}
                onChange={(e) => {
                  setOnlyFillEmptyVin?.(e.target.checked);
                  if (onlyFillEmptyRef) {
                    onlyFillEmptyRef.current = e.target.checked;
                  }
                }}
              />
            }
            label="Only fill empty fields (don't overwrite manual input)"
            sx={{ mt: 0.5 }}
          />
        </Box>
      );
    },
  },
  { name: "year", label: "Year", type: "text" },
  { name: "make", label: "Make", type: "text" },
  { name: "model", label: "Model", type: "text" },
  { name: "tag_number", label: "Tag / Truck Number", type: "text" },
  { name: "engine_size", label: "Engine Size (L)", type: "text" },
  { name: "engine_cylinders", label: "Engine Cylinders", type: "text" },
  {
    name: "drivetrain",
    label: "Drivetrain",
    type: "select",
    options: [
      { label: "Not specified", value: "" },
      { label: "2WD", value: "2WD" },
      { label: "4WD", value: "4WD" },
      { label: "AWD", value: "AWD" },
      { label: "FWD", value: "FWD" },
      { label: "RWD", value: "RWD" },
    ],
  },
];
